import React from "react";

import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { CircleAlert, Loader2 } from "lucide-react";

import { ApiError } from "@/api/client";
import victoriaLogo from "@/assets/logo.svg";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/global/AuthContext";
import { DevelopmentTenantSwitcher } from "@/global/TenantGuard/TenantSwitcher";
import { isDevelopment } from "@/utils/helpers";

export default function LoginView() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [pending, setPending] = React.useState(false);

  const { login, isLoggedIn } = useAuth();
  const { t } = useTranslation();

  React.useEffect(() => {
    document.title = "Login - victoria";
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    login(email, password)
      .then(() => setError(null))
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 401
            ? t("login.errors.invalidCredentials")
            : err instanceof Error
              ? err.message
              : String(err),
        );
      })
      .finally(() => setPending(false));
  };

  if (isLoggedIn) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <Card>
          <CardHeader className="justify-items-center text-center">
            <img
              src={victoriaLogo}
              alt="victoria"
              className="mb-2 h-10 w-auto"
            />
            <CardTitle className="text-xl">{t("login.welcome")}</CardTitle>
            <CardDescription>{t("login.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
              {error && (
                <Alert variant="destructive">
                  <CircleAlert />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid gap-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">{t("login.password")}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <Button type="submit" className="w-full" disabled={pending}>
                {pending && <Loader2 className="animate-spin" />}
                {t("login.login")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {isDevelopment() && <DevelopmentTenantSwitcher />}
      </div>
    </div>
  );
}
